/**
 * useAtMention — Manages @-mention autocomplete state for the chat input.
 *
 * Detects `@` trigger in textarea, filters database objects by fuzzy match,
 * handles keyboard navigation (arrows, Enter, Escape), and inserts selection.
 * Supports column suggestions via dot-chaining and context-aware @ popup.
 */

import { useState, useCallback, useMemo } from 'react';
import type { DatabaseObject } from '../../database-objects/models/databaseObjectTypes';
import type { ColumnInfo } from '../../database-objects/services/columnsService';

export type MentionMode = 'objects' | 'columns' | 'mixed';

export type MentionState = {
  isOpen: boolean;
  query: string;
  /** Character index of the `@` trigger in the textarea value. */
  triggerIndex: number;
  selectedIndex: number;
  /** Current display mode: objects-only, columns-only (dot-chain), or mixed (context-aware @). */
  mode: MentionMode;
  /** Columns to display (for column or mixed mode). */
  columns: ColumnInfo[];
  /** Parent object name for column context (e.g., "dbo.Products"). */
  parentObjectName: string | null;
  /** Whether columns are currently being fetched (for async loading). */
  columnsLoading: boolean;
};

const INITIAL_STATE: MentionState = {
  isOpen: false,
  query: '',
  triggerIndex: -1,
  selectedIndex: 0,
  mode: 'objects',
  columns: [],
  parentObjectName: null,
  columnsLoading: false,
};

const MAX_ITEMS = 50;

/** Simple fuzzy match: every character of the query must appear in order in the target. */
export function fuzzyMatch(query: string, target: string): boolean {
  const q = query.toLowerCase();
  const t = target.toLowerCase();
  let qi = 0;
  for (let ti = 0; ti < t.length && qi < q.length; ti++) {
    if (t[ti] === q[qi]) qi++;
  }
  return qi === q.length;
}

/** Group objects by type in display order: tables, views, procedures, functions. */
function groupAndFilter(objects: DatabaseObject[], query: string): DatabaseObject[] {
  const filtered = query
    ? objects.filter((o) => fuzzyMatch(query, `${o.schema}.${o.name}`))
    : objects;

  const order: Record<string, number> = { table: 0, view: 1, 'stored-procedure': 2, function: 3 };
  const sorted = [...filtered].sort((a, b) => (order[a.type] ?? 4) - (order[b.type] ?? 4));
  return sorted.slice(0, MAX_ITEMS);
}

/** Filter columns by fuzzy match on name. */
function filterColumns(columns: ColumnInfo[], query: string): ColumnInfo[] {
  if (!query) return columns.slice(0, MAX_ITEMS);
  return columns.filter((c) => fuzzyMatch(query, c.name)).slice(0, MAX_ITEMS);
}

/** Total item count for mixed mode (columns + objects). */
function getMixedTotalCount(columns: ColumnInfo[], objects: DatabaseObject[], query: string): number {
  const filteredCols = query ? columns.filter((c) => fuzzyMatch(query, c.name)) : columns;
  const filteredObjs = query
    ? objects.filter((o) => fuzzyMatch(query, `${o.schema}.${o.name}`))
    : objects;
  return filteredCols.length + filteredObjs.length;
}

export function useAtMention(objects: DatabaseObject[] | undefined) {
  const [state, setState] = useState<MentionState>(INITIAL_STATE);

  const filteredColumns = useMemo(
    () => filterColumns(state.columns, state.query),
    [state.columns, state.query]
  );

  const filteredObjects = useMemo(
    () => groupAndFilter(objects ?? [], state.query),
    [objects, state.query]
  );

  /** Combined item count for current mode. */
  const totalItemCount = useMemo(() => {
    if (state.mode === 'columns') return filteredColumns.length;
    if (state.mode === 'mixed') return filteredColumns.length + filteredObjects.length;
    return filteredObjects.length;
  }, [state.mode, filteredColumns.length, filteredObjects.length]);

  const hasMore = useMemo(() => {
    if (!objects) return false;
    if (state.mode === 'columns') {
      const total = state.query
        ? state.columns.filter((c) => fuzzyMatch(state.query, c.name)).length
        : state.columns.length;
      return total > MAX_ITEMS;
    }
    if (state.mode === 'mixed') {
      return getMixedTotalCount(state.columns, objects, state.query) > MAX_ITEMS;
    }
    const total = state.query
      ? objects.filter((o) => fuzzyMatch(state.query, `${o.schema}.${o.name}`)).length
      : objects.length;
    return total > MAX_ITEMS;
  }, [objects, state.query, state.mode, state.columns]);

  const remainingCount = useMemo(() => {
    if (!objects || !hasMore) return 0;
    if (state.mode === 'columns') {
      const total = state.query
        ? state.columns.filter((c) => fuzzyMatch(state.query, c.name)).length
        : state.columns.length;
      return total - MAX_ITEMS;
    }
    if (state.mode === 'mixed') {
      return getMixedTotalCount(state.columns, objects, state.query) - MAX_ITEMS;
    }
    const total = state.query
      ? objects.filter((o) => fuzzyMatch(state.query, `${o.schema}.${o.name}`)).length
      : objects.length;
    return total - MAX_ITEMS;
  }, [objects, state.query, hasMore, state.mode, state.columns]);

  const open = useCallback((triggerIndex: number) => {
    setState({ ...INITIAL_STATE, isOpen: true, triggerIndex });
  }, []);

  /** Open popup in column mode (for dot-chaining). */
  const enterColumnMode = useCallback((triggerIndex: number, columns: ColumnInfo[], parentName: string) => {
    setState({
      isOpen: true,
      query: '',
      triggerIndex,
      selectedIndex: 0,
      mode: 'columns',
      columns,
      parentObjectName: parentName,
      columnsLoading: false,
    });
  }, []);

  /** Open popup in mixed mode (columns from last-mentioned table + objects). */
  const openWithColumns = useCallback((triggerIndex: number, columns: ColumnInfo[], parentName: string) => {
    setState({
      isOpen: true,
      query: '',
      triggerIndex,
      selectedIndex: 0,
      mode: 'mixed',
      columns,
      parentObjectName: parentName,
      columnsLoading: false,
    });
  }, []);

  const close = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  /** Update columns in the current state (for async loading after enterColumnMode). */
  const updateColumns = useCallback((columns: ColumnInfo[]) => {
    setState((s) => ({ ...s, columns, columnsLoading: false, selectedIndex: 0 }));
  }, []);

  /** Set the columns loading flag. */
  const setColumnsLoading = useCallback((loading: boolean) => {
    setState((s) => ({ ...s, columnsLoading: loading }));
  }, []);

  /** Switch mode explicitly (e.g., back to objects when dot is deleted). */
  const setMode = useCallback((mode: MentionMode) => {
    setState((s) => ({
      ...s,
      mode,
      selectedIndex: 0,
      // Clear columns state when leaving column mode
      ...(mode === 'objects' ? { columns: [], parentObjectName: null, columnsLoading: false } : {}),
    }));
  }, []);

  const setQuery = useCallback((query: string) => {
    setState((s) => ({ ...s, query, selectedIndex: 0 }));
  }, []);

  const moveSelection = useCallback((delta: number) => {
    setState((s) => {
      const max = Math.max(0, totalItemCount - 1);
      let next = s.selectedIndex + delta;
      if (next < 0) next = max;
      if (next > max) next = 0;
      return { ...s, selectedIndex: next };
    });
  }, [totalItemCount]);

  const getSelectedObject = useCallback((): DatabaseObject | null => {
    if (state.mode === 'columns') return null;
    if (state.mode === 'mixed') {
      const colCount = filteredColumns.length;
      if (state.selectedIndex >= colCount) {
        return filteredObjects[state.selectedIndex - colCount] ?? null;
      }
      return null;
    }
    return filteredObjects[state.selectedIndex] ?? null;
  }, [filteredObjects, filteredColumns.length, state.selectedIndex, state.mode]);

  const getSelectedColumn = useCallback((): ColumnInfo | null => {
    if (state.mode === 'columns') {
      return filteredColumns[state.selectedIndex] ?? null;
    }
    if (state.mode === 'mixed') {
      if (state.selectedIndex < filteredColumns.length) {
        return filteredColumns[state.selectedIndex] ?? null;
      }
      return null;
    }
    return null;
  }, [filteredColumns, state.selectedIndex, state.mode]);

  return {
    state,
    filteredObjects,
    filteredColumns,
    hasMore,
    remainingCount,
    totalItemCount,
    open,
    enterColumnMode,
    openWithColumns,
    close,
    setQuery,
    setMode,
    updateColumns,
    setColumnsLoading,
    moveSelection,
    getSelectedObject,
    getSelectedColumn,
  };
}
